import { AvatarGroup, Divider, IconButton, Stack, Theme, alpha, useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { atom, useAtom } from 'jotai';
import { KeyboardTabOutlined } from '@mui/icons-material';
import { JourneySatisfaction } from '../JourneySatisfaction';
import { z } from 'zod';
import { useTypedPathParams } from '../../../../shared/fe/hooks/useTypedPathParams';
import { useApiQuery } from '../../query/hooks';
import { graphql } from '../../gql';
import { PersonaAvatarSmall } from '../../persona/PersonaAvatar';
import { useDrawerContext } from '../../drawer/DrawerContext';
import { Box } from '@mui/material';
import { useLocation } from 'react-router';

export const topRowHeightAtom = atom(0);

const queryJourneySummary = graphql(`
	query journey_summaryBanner($id: UUID!) {
		getJourney(by: { id: $id }) {
			satisfaction
			personas {
				id
				...PersonaAvatar_persona
			}
		}
	}
`);

const qpSchema = z.object({
	id: z.string(),
});

type Props = Readonly<{
	offsetRight: number;
}>;

export const SummaryBanner: FC<Props> = ({ offsetRight }) => {
	const { id } = useTypedPathParams(qpSchema);
	const [offset] = useAtom(topRowHeightAtom);
	const { setDrawer } = useDrawerContext();
	const { data: journey, isLoading } = useApiQuery(queryJourneySummary, { variables: { id } });
	const matchUpSM = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
	const location = useLocation();

	if (isLoading || location.pathname.includes('planning')) {
		return null;
	}

	return (
		<Stack
			p={{ xs: 0.75, sm: 1.5 }}
			bgcolor={(theme) => theme.palette[getNightOrDayPalette(theme)][0]}
			boxShadow={(theme) => `0 2px 8px ${alpha(theme.palette[getNightOrDayPalette(theme)].A100 as string, 0.15)}`}
			position={{ xs: 'fixed', sm: 'absolute' }}
			right={{ sm: offsetRight + 8 }}
			left={{ xs: '50%', sm: 'unset' }}
			bottom={{ xs: 8, sm: 'unset' }}
			flexDirection={{ xs: 'row', sm: 'column' }}
			zIndex={6}
			borderRadius={1.5}
			alignItems={{ xs: 'stretch' }}
			top={{ sm: offset + 24 }}
			sx={{ transition: 'right 0.15s ease, top 0.15s ease', transform: { xs: 'translateX(-50%)', sm: 'unset' } }}
		>
			<IconButton sx={{ transform: 'rotate(180deg)' }} onClick={() => setDrawer('journeySummary', { journeyId: id })}>
				<KeyboardTabOutlined />
			</IconButton>
			{!matchUpSM && <Box width={'1px'} mr={0.5} bgcolor={(theme) => theme.palette[getNightOrDayPalette(theme)][200]} />}
			{matchUpSM && <Divider sx={{ mt: 0.5 }} />}
			{journey?.getJourney && (
				<Stack pt={{ sm: 1.5 }} gap={1.5} flexDirection={{ xs: 'row', sm: 'column' }}>
					<JourneySatisfaction satisfaction={journey.getJourney.satisfaction ?? 0} direction={matchUpSM ? 'column' : 'row'} />
					<AvatarGroup
						max={5}
						sx={{
							flexDirection: { xs: 'row-reverse', sm: 'column-reverse' },
							alignItems: 'center',
							justifyContent: 'center',
							'.MuiAvatar-root': {
								ml: { sm: 0 },
							},
							'.MuiAvatar-root:not(:last-of-type)': {
								mt: { sm: -1 },
							},
							'.MuiAvatarGroup-avatar': {
								fontSize: (theme) => theme.typography.body2.fontSize,
								width: 24,
								height: 24,
								border: 'none',
								backgroundColor: 'transparent',
								color: (theme) => theme.palette[getNightOrDayPalette(theme)][600],
								pt: 0.25,
								pl: 0.5,
							},
						}}
					>
						{journey.getJourney.personas?.map((persona) => <PersonaAvatarSmall key={persona.id} persona={persona} />)}
					</AvatarGroup>
				</Stack>
			)}
		</Stack>
	);
};
